import { gql, useApolloClient, useMutation, useQuery } from '@base-app/web2'
import { useEffect } from 'react'

interface AuthUser {
  id: string
  username: string
  email: string
  features: string[]
  roles: string[]
}

const CURRENT_USER = gql`
  query CurrentUser {
    currentUser {
      id
      username
      email
      features
      roles
    }
  }
`

const LOGOUT_MUTATION = gql`
  mutation Logout {
    logout
  }
`

const popstateListeners = new Set<() => void>()
let isListening = false

const handleGlobalPopState = () => {
  popstateListeners.forEach(cb => {
    cb()
  })
}

const subscribeToPopstate = (callback: () => void) => {
  popstateListeners.add(callback)
  if (!isListening) {
    window.addEventListener('popstate', handleGlobalPopState)
    isListening = true
  }
  return () => {
    popstateListeners.delete(callback)
    if (popstateListeners.size === 0) {
      window.removeEventListener('popstate', handleGlobalPopState)
      isListening = false
    }
  }
}

// The user each Apollo client last saw signed in (null: nobody, undefined: no answer yet). It is
// shared by every useAuth() caller so a session change clears the cache once, not once per caller.
const lastUser = new WeakMap<object, string | null>()

export const useAuth = () => {
  const client = useApolloClient()
  const { data, loading, refetch } = useQuery<{ currentUser: AuthUser | null }>(CURRENT_USER, {
    errorPolicy: 'ignore',
    fetchPolicy: 'network-only',
  })

  // Re-check auth on navigation events
  useEffect(() => {
    return subscribeToPopstate(() => refetch())
  }, [refetch])

  const [logoutMutation] = useMutation(LOGOUT_MUTATION)

  // The cache holds the signed-in user's viewer-specific fields (viewerPermissions and the like),
  // so it is dropped on EVERY session change, not only on logout: an expired or revoked session
  // (the user turns into null) or another user showing up in place of the first one.
  const currentId = data?.currentUser?.id ?? null
  const answered = data !== undefined
  useEffect(() => {
    // No data is not "nobody signed in": it is also what a query reports right after the store
    // was cleared, and reading that as the session ending would clear again, forever
    if (loading || !answered) {
      return
    }
    const previous = lastUser.get(client)
    lastUser.set(client, currentId)
    if (previous && previous !== currentId) {
      // Another user took over: reset (clear AND refetch the active queries), or the CurrentUser
      // query watching right now would be left without an answer and bounce the app to /login.
      // Nobody signed in any more: a plain clear is enough.
      const drop = currentId ? client.resetStore() : client.clearStore()
      drop.catch(() => {})
    }
  }, [client, currentId, loading, answered])

  const logout = async () => {
    try {
      await logoutMutation()
    } catch (_) {
      // swallow errors — proceed with client-side logout
    } finally {
      // Mark nobody as signed in first, so the user turning null below isn't cleared a second time
      lastUser.set(client, null)
      // Cached data belongs to this user (viewerPermissions and the like): drop it even when the
      // logout request failed, so the next person to sign in on this browser starts clean
      try {
        await client.clearStore()
      } catch (_) {
        // nothing to clear
      }
    }
    window.history.pushState({}, '', '/login')
    window.dispatchEvent(new Event('popstate'))
  }

  // A new session starts from an empty cache: nothing left over from whoever was here before.
  // resetStore clears AND refetches the active queries; a plain clear would leave the app's own
  // CurrentUser query without an answer, and the user would bounce back to /login.
  const startSession = async () => {
    await client.resetStore().catch(() => {})
  }

  const user = data?.currentUser ?? null
  const isAuthenticated = !!user
  const authChecked = !loading

  return { isAuthenticated, authChecked, user, logout, startSession, refetch }
}
